import { useCallback, useMemo, useState } from 'react';

/**
 * Generic helper for the "N new since you opened this page" pattern on list
 * views. Holds an anchor timestamp in component state — initialised to
 * `Date.now()` on mount, so the count starts at 0 and only climbs as
 * subsequent refetches surface items whose timestamp is newer than the
 * anchor. `acknowledge()` snaps the anchor back to now, clearing the count
 * and any per-row highlighting.
 *
 * Designed to be reusable for any list page (Support, Conversations, and
 * later Tickets / Customers / Leads). Defaults to reading `item.createdAt`
 * but accepts a `getTimestamp` accessor for records that use a different
 * field (e.g. conversations key off `lastMessageAt` since that's what the
 * inbox sorts by — a brand new reply on an existing thread is "new" too).
 *
 * Mock-mode coverage: when the underlying query refetches against a fixture
 * source, the same array is returned each time — so no item's timestamp
 * crosses the anchor and `newCount` stays at 0. Correct behaviour: we never
 * want false positives from data that didn't actually change.
 */
export interface NewSinceAnchor<T> {
  /** Count of items whose timestamp is strictly after the anchor. */
  newCount: number;
  /** True when the given item's timestamp is strictly after the anchor. */
  isNew: (item: T) => boolean;
  /** Snap the anchor to `Date.now()` — clears `newCount` to 0. */
  acknowledge: () => void;
}

type TimestampInput = string | number | Date;

function readTimestamp(value: TimestampInput): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  // ISO string — `new Date(...)` handles the parse.
  return new Date(value).getTime();
}

export function useNewSinceAnchor<T extends { createdAt: string | Date }>(
  items: readonly T[] | undefined,
): NewSinceAnchor<T>;

export function useNewSinceAnchor<T>(
  items: readonly T[] | undefined,
  getTimestamp: (item: T) => TimestampInput,
): NewSinceAnchor<T>;

export function useNewSinceAnchor<T>(
  items: readonly T[] | undefined,
  getTimestamp?: (item: T) => TimestampInput,
): NewSinceAnchor<T> {
  const [anchor, setAnchor] = useState<number>(() => Date.now());

  const accessor = useMemo<(item: T) => TimestampInput>(() => {
    if (getTimestamp) return getTimestamp;
    return (item) => (item as unknown as { createdAt: string | Date }).createdAt;
  }, [getTimestamp]);

  const isNew = useCallback(
    (item: T) => {
      const raw = accessor(item);
      if (raw == null) return false;
      const ts = readTimestamp(raw);
      if (Number.isNaN(ts)) return false;
      return ts > anchor;
    },
    [accessor, anchor],
  );

  const newCount = useMemo(() => {
    if (!items || items.length === 0) return 0;
    let count = 0;
    for (const item of items) {
      if (isNew(item)) count += 1;
    }
    return count;
  }, [items, isNew]);

  const acknowledge = useCallback(() => {
    setAnchor(Date.now());
  }, []);

  return { newCount, isNew, acknowledge };
}
